
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

interface SecurityLogsFilterProps {
  filters: {
    actionType: string;
    userId: string;
    dateFrom?: Date;
    dateTo?: Date;
  };
  setFilters: React.Dispatch<React.SetStateAction<{
    actionType: string;
    userId: string;
    dateFrom?: Date;
    dateTo?: Date;
  }>>;
}

export const SecurityLogsFilter = ({
  filters,
  setFilters
}: SecurityLogsFilterProps) => {
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  
  const actionTypes = [
    { value: "", label: "Todos os tipos" },
    { value: "login", label: "Login" },
    { value: "logout", label: "Logout" },
    { value: "config_change", label: "Alteração de configuração" },
    { value: "token_use", label: "Uso de token" },
    { value: "webhook_call", label: "Chamada de webhook" }
  ];
  
  const clearFilters = () => {
    setFilters({
      actionType: "",
      userId: "",
      dateFrom: undefined,
      dateTo: undefined
    });
  };
  
  const handleDateFromChange = (date: Date | undefined) => {
    setFilters(prev => ({
      ...prev,
      dateFrom: date
    }));
  };
  
  const handleDateToChange = (date: Date | undefined) => {
    setFilters(prev => ({
      ...prev,
      dateTo: date
    }));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setIsFiltersVisible(!isFiltersVisible)}
          className="gap-2"
        >
          <Filter className="h-4 w-4" />
          Filtros
        </Button>
        
        {(filters.actionType || filters.userId || filters.dateFrom || filters.dateTo) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="gap-1 text-muted-foreground"
          >
            <X className="h-3 w-3" />
            Limpar filtros
          </Button>
        )}
      </div>
      
      {isFiltersVisible && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Tipo de ação</label>
            <Select
              value={filters.actionType}
              onValueChange={(value) => setFilters(prev => ({ ...prev, actionType: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os tipos" />
              </SelectTrigger>
              <SelectContent>
                {actionTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">ID/Nome do usuário</label>
            <Input
              value={filters.userId}
              onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))}
              placeholder="Filtrar por usuário"
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Data inicial</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !filters.dateFrom && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateFrom ? (
                    format(filters.dateFrom, "PPP", { locale: ptBR })
                  ) : (
                    <span>Escolha uma data</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.dateFrom}
                  onSelect={handleDateFromChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-1 block">Data final</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !filters.dateTo && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.dateTo ? (
                    format(filters.dateTo, "PPP", { locale: ptBR })
                  ) : (
                    <span>Escolha uma data</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.dateTo}
                  onSelect={handleDateToChange}
                  initialFocus
                  disabled={(date) => 
                    filters.dateFrom ? date < filters.dateFrom : false
                  }
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}
    </div>
  );
};
